type TaxIdType =
  | "EU_VAT"
  | "GB_VAT"
  | "CH_VAT"
  | "NO_VAT"
  | "US_EIN"
  | "CA_BN"
  | "AU_ABN"
  | "NZ_GST"
  | "IN_GST"
  | "JP_CN"
  | "KR_BRN"
  | "BR_CNPJ"
  | "MX_RFC"
  | "SG_GST"
  | "TH_VAT"
  | "ZA_VAT"
  | "MY_FRP"
  | "TR_TIN"
  | "TW_VAT"
  | "SA_VAT"
  | "CL_TIN"
  | "IL_VAT"
  | "EG_TIN"
  | "PH_TIN"
  | "CO_NIT"
  | "IS_VAT"
  | "LI_UID"
  | "HK_BR"
  | "ID_NPWP"
  | "KE_PIN"
  | "NG_TIN"
  | "AE_TRN"
  | "AR_CUIT"
  | "BO_TIN"
  | "CR_TIN"
  | "DO_RCN"
  | "EC_RUC"
  | "PE_RUC"
  | "UY_RUC"
  | "VE_RIF"
  | "VN_TIN";

export type TaxIdValidationError = "invalidTaxId" | "invalidTaxIdCountry";

function resolveTaxIdType(country?: string): TaxIdType | null {
  if (!country || !country.trim()) return null;
  const countryCode = country.trim().toUpperCase();

  switch (countryCode) {
    case "AT":
    case "BE":
    case "BG":
    case "HR":
    case "CY":
    case "CZ":
    case "DK":
    case "EE":
    case "FI":
    case "FR":
    case "DE":
    case "GR":
    case "HU":
    case "IE":
    case "IT":
    case "LV":
    case "LT":
    case "LU":
    case "MT":
    case "NL":
    case "PL":
    case "PT":
    case "RO":
    case "SK":
    case "SI":
    case "ES":
    case "SE":
      return "EU_VAT";
    case "GB":
      return "GB_VAT";
    case "CH":
      return "CH_VAT";
    case "NO":
      return "NO_VAT";
    case "US":
      return "US_EIN";
    case "CA":
      return "CA_BN";
    case "AU":
      return "AU_ABN";
    case "NZ":
      return "NZ_GST";
    case "IN":
      return "IN_GST";
    case "JP":
      return "JP_CN";
    case "KR":
      return "KR_BRN";
    case "BR":
      return "BR_CNPJ";
    case "MX":
      return "MX_RFC";
    case "SG":
      return "SG_GST";
    case "TH":
      return "TH_VAT";
    case "ZA":
      return "ZA_VAT";
    case "MY":
      return "MY_FRP";
    case "TR":
      return "TR_TIN";
    case "TW":
      return "TW_VAT";
    case "SA":
      return "SA_VAT";
    case "CL":
      return "CL_TIN";
    case "IL":
      return "IL_VAT";
    case "EG":
      return "EG_TIN";
    case "PH":
      return "PH_TIN";
    case "CO":
      return "CO_NIT";
    case "IS":
      return "IS_VAT";
    case "LI":
      return "LI_UID";
    case "HK":
      return "HK_BR";
    case "ID":
      return "ID_NPWP";
    case "KE":
      return "KE_PIN";
    case "NG":
      return "NG_TIN";
    case "AE":
      return "AE_TRN";
    case "AR":
      return "AR_CUIT";
    case "BO":
      return "BO_TIN";
    case "CR":
      return "CR_TIN";
    case "DO":
      return "DO_RCN";
    case "EC":
      return "EC_RUC";
    case "PE":
      return "PE_RUC";
    case "UY":
      return "UY_RUC";
    case "VE":
      return "VE_RIF";
    case "VN":
      return "VN_TIN";
    default:
      return null;
  }
}

function isPlausibleTaxIdFormat(value: string, type: TaxIdType): boolean {
  if (!value || !value.trim()) return false;
  const trimmed = value.trim();

  switch (type) {
    case "EU_VAT":
      return /^[A-Za-z]{2}[0-9A-Za-z]{2,13}$/.test(trimmed);
    case "GB_VAT":
      return /^(GB)?\d{9}(\d{3})?$/.test(trimmed.replaceAll(/\s/g, ""));
    case "CH_VAT":
      return /^CHE\d{9}(MWST|TVA|IVA)?$/i.test(
        trimmed.replaceAll(/[\s.]/g, ""),
      );
    case "US_EIN":
      return /^\d{9}$/.test(trimmed.replaceAll(/-/g, ""));
    default:
      return trimmed.length >= 2;
  }
}

export function getCompanyTaxIdValidationError(params: {
  customerType?: string;
  country?: string;
  taxId?: string;
}): TaxIdValidationError | null {
  const { customerType, country, taxId } = params;

  if (customerType !== "COMPANY") return null;
  if (!taxId || !taxId.trim()) return null;

  const taxIdType = resolveTaxIdType(country);
  if (!taxIdType) return "invalidTaxIdCountry";

  if (!isPlausibleTaxIdFormat(taxId, taxIdType)) return "invalidTaxId";

  return null;
}
